// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/Transfer.tsx
// 
// این فایل را در همان مسیر بالا کپی کنید (روی فایل قبلی بنویسید)
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, accountsAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import PersianDatePicker, { todayGregorian, gregorianToJalaliStr, DualDate } from "../../../utils/PersianDatePicker";
import { Plus, Trash2, ArrowLeft, Edit2 } from "lucide-react";
import JournalFilter, { emptyFilter, applyFilter, JournalFilterValues, exportToExcel } from "./JournalFilter";
import {
  StatusTabs, PendingBadge, ConfirmIconButton, RevertIconButton,
  PendingSubmitButton, ConfirmSubmitButton, PendingHint, pendingRowStyle,
} from "./PendingControls";
import {
  inputSt, rowSt, badgeSt, addBtnSt, iconBtnSt,
  Lbl, BtnCancel, BtnSave, Toast, Loading, Empty, Modal,
} from "./Purchase";

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const emptyForm = {
  date: todayGregorian(),
  from_account_id: "", from_account_name: "",
  to_account_id: "", to_account_name: "",
  from_amount: "", exchange_rate: "1",
  commission: "0", commission_from: "source",
  notes: "", attachment: "",
};

export default function Transfer() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [list, setList]         = useState<any[]>([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [cashAccs, setCashAccs] = useState<any[]>([]);
  const [filter, setFilter]     = useState<JournalFilterValues>(emptyFilter);
  const [toast, setToast]       = useState({ text: "", ok: true });
  const [form, setForm]         = useState(emptyForm);
  const [statusFilter, setStatusFilter] = useState<"all"|"pending"|"confirmed">("all");

  const load = useCallback(async () => {
    setLoading(true);
    const [t, a] = await Promise.all([journalAPI.listTransfers(), accountsAPI.getCashAccounts()]);
    if (t.success) setList(t.transfers || []);
    if (a.success) setCashAccs(a.accounts || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const calcToAmount = (f: any, accs: any[]) => {
    const amt  = parseFloat(f.from_amount || "0");
    const rate = parseFloat(f.exchange_rate || "1");
    if (!amt || !rate) return 0;
    const toAcc = accs.find((a: any) => String(a.id) === f.to_account_id);
    if (!toAcc) return amt;
    return toAcc.currency === "USD" ? amt / rate : amt * rate;
  };

  const toAmount     = calcToAmount(form, cashAccs);
  const editToAmount = editItem ? calcToAmount(editItem, cashAccs) : 0;

  const fromAcc     = cashAccs.find((a: any) => String(a.id) === form.from_account_id);
  const toAcc       = cashAccs.find((a: any) => String(a.id) === form.to_account_id);
  const editFromAcc = editItem ? cashAccs.find((a: any) => String(a.id) === editItem.from_account_id) : null;
  const editToAcc   = editItem ? cashAccs.find((a: any) => String(a.id) === editItem.to_account_id) : null;

  const calcDeduct = (f: any) => f.commission_from === "source"
    ? parseFloat(f.from_amount || "0") + parseFloat(f.commission || "0")
    : parseFloat(f.from_amount || "0");

  const calcAdd = (toAmt: number, f: any) => f.commission_from === "destination"
    ? toAmt - parseFloat(f.commission || "0")
    : toAmt;

  const validate = () => {
    if (!form.from_account_id) { showToast("حساب مبدا الزامی است", false); return false; }
    if (!form.to_account_id)   { showToast("حساب مقصد الزامی است", false); return false; }
    if (!form.from_amount)     { showToast("مبلغ الزامی است", false); return false; }
    if (form.from_account_id === form.to_account_id) { showToast("حساب مبدا و مقصد نمی‌توانند یکسان باشند", false); return false; }
    return true;
  };

  const buildSubmitData = (statusValue: "pending"|"confirmed") => ({
    date: form.date,
    from_account_id: parseInt(form.from_account_id), from_account_name: fromAcc?.name || "",
    to_account_id: parseInt(form.to_account_id), to_account_name: toAcc?.name || "",
    from_amount: parseFloat(form.from_amount), exchange_rate: parseFloat(form.exchange_rate || "1"),
    commission: parseFloat(form.commission || "0"), commission_from: form.commission_from,
    notes: form.notes, attachment: form.attachment,
    status: statusValue,
  });

  const handleSubmitConfirmed = async () => {
    if (!validate()) return;
    const res = await journalAPI.addTransfer(buildSubmitData("confirmed"));
    if (res.success) {
      showToast(`انتقال ${res.serial} با موفقیت ثبت شد`);
      setShowForm(false); setForm({ ...emptyForm, date: todayGregorian() }); load();
    } else showToast(res.message || "خطا", false);
  };

  const handleSubmitPending = async () => {
    if (!validate()) return;
    const res = await journalAPI.addTransfer(buildSubmitData("pending"));
    if (res.success) {
      showToast(`انتقال ${res.serial} در انتظار تأیید ذخیره شد`);
      setShowForm(false); setForm({ ...emptyForm, date: todayGregorian() }); load();
    } else showToast(res.message || "خطا", false);
  };

  const openEdit = (t: any) => setEditItem({
    id: t.id, date: t.date,
    from_account_id: String(t.from_account_id), from_account_name: t.from_account_name,
    to_account_id: String(t.to_account_id), to_account_name: t.to_account_name,
    from_amount: String(t.from_amount), exchange_rate: String(t.exchange_rate || "1"),
    commission: String(t.commission || "0"), commission_from: t.commission_from || "source",
    notes: t.notes || "", attachment: t.attachment || "",
    status: t.status || "confirmed",
  });

  const handleEdit = async () => {
    if (!editItem.from_account_id) { showToast("حساب مبدا الزامی است", false); return; }
    if (!editItem.to_account_id)   { showToast("حساب مقصد الزامی است", false); return; }
    if (!editItem.from_amount)     { showToast("مبلغ الزامی است", false); return; }
    if (editItem.from_account_id === editItem.to_account_id) { showToast("حساب مبدا و مقصد نمی‌توانند یکسان باشند", false); return; }
    const res = await journalAPI.updateTransfer({
      id: editItem.id, date: editItem.date,
      from_account_id: parseInt(editItem.from_account_id), from_account_name: editFromAcc?.name || editItem.from_account_name,
      to_account_id: parseInt(editItem.to_account_id), to_account_name: editToAcc?.name || editItem.to_account_name,
      from_amount: parseFloat(editItem.from_amount), exchange_rate: parseFloat(editItem.exchange_rate || "1"),
      commission: parseFloat(editItem.commission || "0"), commission_from: editItem.commission_from,
      notes: editItem.notes, attachment: editItem.attachment,
    });
    if (res.success) { showToast("انتقال ویرایش شد"); setEditItem(null); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleDelete = async (id: number, serial: string) => {
    if (!window.confirm(`حذف انتقال ${serial}؟`)) return;
    const res = await journalAPI.deleteTransfer(id);
    if (res.success) { showToast("انتقال حذف شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleConfirm = async (id: number, serial: string) => {
    if (!window.confirm(`تأیید نهایی انتقال ${serial}؟\nمبلغ از حساب مبدا کم و به حساب مقصد اضافه می‌شود.`)) return;
    const res = await journalAPI.confirmTransfer(id);
    if (res.success) { showToast("انتقال تأیید شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleRevert = async (id: number, serial: string) => {
    if (!window.confirm(`برگرداندن انتقال ${serial} به حالت در انتظار؟\nتأثیر مالی آن لغو می‌شود.`)) return;
    const res = await journalAPI.revertTransfer(id);
    if (res.success) { showToast("به حالت در انتظار برگشت"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const filtered = applyFilter(list, filter, {
    searchFields: ["serial", "from_account_name", "to_account_name", "notes"],
    dateField: "date",
    amountField: "from_amount",
    accountField: "from_account_id",
  }).filter((t: any) => {
    const st = t.status || "confirmed";
    if (statusFilter === "all") return true;
    return st === statusFilter;
  });

  const pendingCount = list.filter((t:any) => (t.status||"confirmed") === "pending").length;

  const accOptions = cashAccs.map((a: any) => ({
    value: String(a.id),
    label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}`,
  }));

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && <Toast text={toast.text} ok={toast.ok} />}

      <JournalFilter
        config={{
          searchPlaceholder: "جستجو در انتقال‌ها... (شماره سند، حساب مبدا، حساب مقصد)",
          showDateRange: true,
          showAmountRange: true,
          showCurrency: false,
          showAccount: true,
          accountOptions: cashAccs.map((a: any) => ({ value: String(a.id), label: a.name })),
        }}
        values={filter}
        onChange={setFilter}
        resultCount={filtered.length}
        totalCount={list.length}
        T={T}
        dark={dark}
            onExport={() => exportToExcel(filtered.map(t => ({
          "شماره سند": t.serial,
          "تاریخ": t.date,
          "از حساب": t.from_account_name,
          "به حساب": t.to_account_name,
          "مبلغ مبدا": t.from_amount,
          "نرخ تبدیل": t.exchange_rate,
          "مبلغ مقصد": t.to_amount,
          "کمیسیون": t.commission || 0,
          "وضعیت": (t.status||"confirmed") === "pending" ? "در انتظار" : "تأیید شده",
          "توضیحات": t.notes || "",
        })), "انتقال_پول")}
      exportLabel="دانلود Excel انتقال"
      />

      {/* فیلتر وضعیت + دکمه ثبت */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <StatusTabs
          value={statusFilter}
          onChange={setStatusFilter}
          totalCount={list.length}
          pendingCount={pendingCount}
          T={T}
          dark={dark}
        />
        {canAdd && <button onClick={() => setShowForm(true)} style={addBtnSt("#06b6d4")}><Plus size={15} /> ثبت انتقال پول</button>}
      </div>

      {loading ? <Loading T={T} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.length === 0 && <Empty T={T} text="هیچ انتقالی ثبت نشده" />}
          {filtered.map((t: any) => {
            const isPending = (t.status || "confirmed") === "pending";
            return (
            <div key={t.id} style={{
              ...rowSt(dark, T),
              ...(isPending ? pendingRowStyle(dark) : {})
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={badgeSt("#06b6d4")}>{t.serial}</span>
                {isPending && <PendingBadge />}
                <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{t.from_account_name}</span>
                <ArrowLeft size={14} color={T.subtext} />
                <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{t.to_account_name}</span>
                <DualDate date={t.date} fontSize={12} color={T.subtext} />
                {t.notes && <span style={{ fontSize: 11, color: T.subtext, fontStyle: "italic" }}>{t.notes}</span>}
                <div style={{ marginRight: "auto", display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontWeight: 800, color: "#ef4444", fontSize: 14 }}>-{fmt(t.from_amount)}</span>
                  <span style={{ fontSize: 12, color: T.subtext }}>→</span>
                  <span style={{ fontWeight: 800, color: "#10b981", fontSize: 14 }}>+{fmt(t.to_amount)}</span>
                  {parseFloat(t.commission) > 0 && <span style={{ fontSize: 11, color: "#f59e0b" }}>کمیشن: {fmt(t.commission)}</span>}
                  {isPending && canEdit && (
                    <ConfirmIconButton onClick={() => handleConfirm(t.id, t.serial)} />
                  )}
                  {!isPending && canEdit && (
                    <RevertIconButton onClick={() => handleRevert(t.id, t.serial)} />
                  )}
                  {canEdit && <button onClick={() => openEdit(t)} title="ویرایش" style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>}
                  {canDelete && <button onClick={() => handleDelete(t.id, t.serial)} title="حذف" style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* فرم ثبت */}
      {showForm && (
        <Modal title="ثبت انتقال پول" onClose={() => setShowForm(false)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} /></div>
            <div>
              <Lbl T={T}>حساب مبدا *</Lbl>
              <SearchableSelect options={accOptions} value={form.from_account_id}
                onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setForm(f => ({ ...f, from_account_id: v, from_account_name: ac?.name || "" })); }} placeholder="انتخاب حساب مبدا" />
            </div>
            <div>
              <Lbl T={T}>حساب مقصد *</Lbl>
              <SearchableSelect options={accOptions.filter(o => o.value !== form.from_account_id)} value={form.to_account_id}
                onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setForm(f => ({ ...f, to_account_id: v, to_account_name: ac?.name || "" })); }} placeholder="انتخاب حساب مقصد" />
            </div>
            <div>
              <Lbl T={T}>مبلغ از حساب مبدا ({fromAcc?.currency || "—"}) *</Lbl>
              <input type="number" value={form.from_amount} onChange={e => setForm(f => ({ ...f, from_amount: e.target.value }))} placeholder="0.00" style={inputSt(T)} />
            </div>
            <div>
              <Lbl T={T}>نرخ تبادله</Lbl>
              <input type="number" value={form.exchange_rate} onChange={e => setForm(f => ({ ...f, exchange_rate: e.target.value }))} placeholder="1" style={inputSt(T)} />
              {fromAcc && toAcc && fromAcc.currency !== toAcc.currency && (
                <div style={{ fontSize: 11, color: T.subtext, marginTop: 4 }}>{toAcc.currency === "USD" ? `${form.from_amount || 0} ÷ ${form.exchange_rate || 1}` : `${form.from_amount || 0} × ${form.exchange_rate || 1}`}</div>
              )}
            </div>
            <div>
              <Lbl T={T}>مبلغ به حساب مقصد ({toAcc?.currency || "—"})</Lbl>
              <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(6,182,212,0.1)" : "#ecfeff", color: "#06b6d4", fontWeight: 700, fontSize: 14, border: "1.5px solid #06b6d420" }}>{fmt(toAmount)} {toAcc?.currency || ""}</div>
            </div>
            <div><Lbl T={T}>کمیشن انتقال</Lbl><input type="number" value={form.commission} onChange={e => setForm(f => ({ ...f, commission: e.target.value }))} placeholder="0" style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>کمیشن از</Lbl>
              <SearchableSelect options={[{ value: "source", label: "حساب مبدا" }, { value: "destination", label: "حساب مقصد" }]} value={form.commission_from} onChange={v => setForm(f => ({ ...f, commission_from: v }))} placeholder="انتخاب" />
            </div>
            <div><Lbl T={T}>توضیحات</Lbl><input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div><Lbl T={T}>ضمیمه</Lbl><input value={form.attachment} onChange={e => setForm(f => ({ ...f, attachment: e.target.value }))} placeholder="لینک..." style={inputSt(T)} /></div>
          </div>
          <div style={{ marginTop: 16, padding: "16px 18px", borderRadius: 12, background: dark ? "rgba(6,182,212,0.08)" : "#ecfeff", border: "1px solid #06b6d420" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 10 }}>خلاصه انتقال</div>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
              <div><span style={{ color: T.subtext }}>کسر از مبدا: </span><strong style={{ color: "#ef4444" }}>{fmt(calcDeduct(form))} {fromAcc?.currency || ""}</strong></div>
              <div><span style={{ color: T.subtext }}>اضافه به مقصد: </span><strong style={{ color: "#10b981" }}>{fmt(calcAdd(toAmount, form))} {toAcc?.currency || ""}</strong></div>
              {parseFloat(form.commission || "0") > 0 && <div><span style={{ color: T.subtext }}>کمیشن: </span><strong style={{ color: "#f59e0b" }}>{fmt(parseFloat(form.commission || "0"))} ({form.commission_from === "source" ? "از مبدا" : "از مقصد"})</strong></div>}
            </div>
          </div>
          <div style={{
            display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center",
            marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}`,
            flexWrap: "wrap"
          }}>
            <PendingHint T={T} />
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <BtnCancel onClick={() => setShowForm(false)} T={T} />
              <PendingSubmitButton
                onClick={() => submit(handleSubmitPending)}
                loading={submitting}
                label="ذخیره انتقال در انتظار"
              />
              <ConfirmSubmitButton
                onClick={() => submit(handleSubmitConfirmed)}
                loading={submitting}
                color="#06b6d4"
                label="ثبت انتقال"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* فرم ویرایش */}
      {editItem && (
        <Modal title={`ویرایش انتقال پول${editItem.status==="pending" ? " (در انتظار)" : ""}`} onClose={() => setEditItem(null)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={editItem.date} onChange={v => setEditItem((f: any) => ({ ...f, date: v }))} /></div>
            <div>
              <Lbl T={T}>حساب مبدا *</Lbl>
              <SearchableSelect options={accOptions} value={editItem.from_account_id}
                onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, from_account_id: v, from_account_name: ac?.name || "" })); }} placeholder="انتخاب حساب مبدا" />
            </div>
            <div>
              <Lbl T={T}>حساب مقصد *</Lbl>
              <SearchableSelect options={accOptions.filter(o => o.value !== editItem.from_account_id)} value={editItem.to_account_id}
                onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, to_account_id: v, to_account_name: ac?.name || "" })); }} placeholder="انتخاب حساب مقصد" />
            </div>
            <div>
              <Lbl T={T}>مبلغ از حساب مبدا ({editFromAcc?.currency || "—"}) *</Lbl>
              <input type="number" value={editItem.from_amount} onChange={e => setEditItem((f: any) => ({ ...f, from_amount: e.target.value }))} placeholder="0.00" style={inputSt(T)} />
            </div>
            <div>
              <Lbl T={T}>نرخ تبادله</Lbl>
              <input type="number" value={editItem.exchange_rate} onChange={e => setEditItem((f: any) => ({ ...f, exchange_rate: e.target.value }))} placeholder="1" style={inputSt(T)} />
              {editFromAcc && editToAcc && editFromAcc.currency !== editToAcc.currency && (
                <div style={{ fontSize: 11, color: T.subtext, marginTop: 4 }}>{editToAcc.currency === "USD" ? `${editItem.from_amount || 0} ÷ ${editItem.exchange_rate || 1}` : `${editItem.from_amount || 0} × ${editItem.exchange_rate || 1}`}</div>
              )}
            </div>
            <div>
              <Lbl T={T}>مبلغ به حساب مقصد ({editToAcc?.currency || "—"})</Lbl>
              <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(6,182,212,0.1)" : "#ecfeff", color: "#06b6d4", fontWeight: 700, fontSize: 14, border: "1.5px solid #06b6d420" }}>{fmt(editToAmount)} {editToAcc?.currency || ""}</div>
            </div>
            <div><Lbl T={T}>کمیشن انتقال</Lbl><input type="number" value={editItem.commission} onChange={e => setEditItem((f: any) => ({ ...f, commission: e.target.value }))} placeholder="0" style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>کمیشن از</Lbl>
              <SearchableSelect options={[{ value: "source", label: "حساب مبدا" }, { value: "destination", label: "حساب مقصد" }]} value={editItem.commission_from} onChange={v => setEditItem((f: any) => ({ ...f, commission_from: v }))} placeholder="انتخاب" />
            </div>
            <div><Lbl T={T}>توضیحات</Lbl><input value={editItem.notes} onChange={e => setEditItem((f: any) => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div><Lbl T={T}>ضمیمه</Lbl><input value={editItem.attachment} onChange={e => setEditItem((f: any) => ({ ...f, attachment: e.target.value }))} placeholder="لینک..." style={inputSt(T)} /></div>
          </div>
          <div style={{ marginTop: 16, padding: "16px 18px", borderRadius: 12, background: dark ? "rgba(6,182,212,0.08)" : "#ecfeff", border: "1px solid #06b6d420" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 10 }}>خلاصه انتقال</div>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
              <div><span style={{ color: T.subtext }}>کسر از مبدا: </span><strong style={{ color: "#ef4444" }}>{fmt(calcDeduct(editItem))} {editFromAcc?.currency || ""}</strong></div>
              <div><span style={{ color: T.subtext }}>اضافه به مقصد: </span><strong style={{ color: "#10b981" }}>{fmt(calcAdd(editToAmount, editItem))} {editToAcc?.currency || ""}</strong></div>
              {parseFloat(editItem.commission || "0") > 0 && <div><span style={{ color: T.subtext }}>کمیشن: </span><strong style={{ color: "#f59e0b" }}>{fmt(parseFloat(editItem.commission || "0"))}</strong></div>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => setEditItem(null)} T={T} />
            <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#06b6d4" label="ذخیره ویرایش" />
          </div>
        </Modal>
      )}
    </div>
  );
}